/**
 * Currency formatting utilities
 */

/**
 * Format a number as currency with thousand separators and proper decimal places
 * @param value - The number to format
 * @param currencySymbol - The currency symbol (e.g., 'R', '$', '€')
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "R 1,234.56")
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currencySymbol = '',
  decimals = 2
): string {
  // Handle null, undefined, or invalid values
  if (value === null || value === undefined || value === '') {
    return `${currencySymbol} 0.${'0'.repeat(decimals)}`;
  }

  // Convert to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid numbers
  if (isNaN(numValue)) {
    return `${currencySymbol} 0.${'0'.repeat(decimals)}`;
  }

  // Format with thousand separators and decimal places
  const formatted = numValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return currencySymbol ? `${currencySymbol} ${formatted}` : formatted;
}

/**
 * Format a number with thousand separators (without currency symbol)
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string (e.g., "1,234.56")
 */
export function formatNumber(
  value: number | string | null | undefined,
  decimals = 2
): string {
  // Handle null, undefined, or invalid values
  if (value === null || value === undefined || value === '') {
    return `0.${'0'.repeat(decimals)}`;
  }

  // Convert to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid numbers
  if (isNaN(numValue)) {
    return `0.${'0'.repeat(decimals)}`;
  }

  // Format with thousand separators and decimal places
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format odds values (preserve decimal places for accurate odds display)
 * @param value - The odds value to format
 * @returns Formatted odds string (e.g., "5.5", "45,000.25")
 */
export function formatOdds(value: number | string | null | undefined): string {
  // Handle null, undefined, or invalid values
  if (value === null || value === undefined || value === '') {
    return '0.00';
  }

  // Convert to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid numbers
  if (isNaN(numValue)) {
    return '0.00';
  }

  // Format with thousand separators, preserve decimal places up to 2 digits
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
